import React, { memo, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Button, ChatTeamResponse, ChatUserResponse, Grid } from "@sumprio/ui";
import { AppChatInput } from "../AppChatInput";
import EmptyState from "../EmptyState";
import { getInitials } from "../NotesAndActivityFeed";
import { formatLocalDateTime, MOMENT_FMT_DD_MMM_YYYY_HH_MM_A } from "utils/dateTime";
import "./ChatConversationPanel.scss";

export interface ChatConversationMessage {
  id: string | number;
  senderName: string;
  senderInitials?: string;
  message: string;
  timestamp: string;
  isFromCurrentUser?: boolean;
}

function formatChatTimestamp(dateStr?: string): string {
  return formatLocalDateTime(dateStr, {
    format: MOMENT_FMT_DD_MMM_YYYY_HH_MM_A,
    assumeUtc: true,
  });
}

export interface ChatConversationPanelProps {
  messages: ChatConversationMessage[];
  threadKey?: string;
  emptyStateTitle: string;
  emptyStateDescription: string;
  emptyStateIcon?: React.ReactNode;
  value: string;
  onChange: (_value: string) => void;
  onSend: (_payload?: { value?: string }) => void;
  disabled?: boolean;
  placeholder?: string;
  loading?: boolean;
  hasMoreOlderMessages?: boolean;
  loadingOlderMessages?: boolean;
  onLoadMoreOlder?: () => void | Promise<void>;
  showAttachmentIcon?: boolean;
  inputClassName?: string;
}

function scrollMessagesToBottom(scrollEl: HTMLDivElement, endEl: HTMLElement | null) {
  scrollEl.scrollTop = scrollEl.scrollHeight;
  if (endEl) {
    try {
      endEl.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
    } catch {
      /* ignore */
    }
  }
  scrollEl.scrollTop = scrollEl.scrollHeight;
  requestAnimationFrame(() => {
    scrollEl.scrollTop = scrollEl.scrollHeight;
    if (endEl) {
      try {
        endEl.scrollIntoView({ block: "end", inline: "nearest", behavior: "auto" });
      } catch {
        /* ignore */
      }
    }
    requestAnimationFrame(() => {
      scrollEl.scrollTop = scrollEl.scrollHeight;
    });
  });
}

function scheduleScrollToBottomRetries(scrollEl: HTMLDivElement, endEl: HTMLElement | null) {
  const delays = [0, 50, 120, 250, 400];
  const ids: number[] = [];
  for (const ms of delays) {
    ids.push(
      window.setTimeout(() => {
        scrollMessagesToBottom(scrollEl, endEl);
      }, ms)
    );
  }
  return () => ids.forEach((id) => window.clearTimeout(id));
}

function messageRowKey(msg: ChatConversationMessage, index: number): string {
  if (msg.id !== undefined && msg.id !== null && String(msg.id).trim() !== "") {
    return String(msg.id);
  }
  return `msg-${index}-${msg.timestamp}-${msg.message.slice(0, 24)}`;
}

interface ChatConversationMessageRowProps {
  msg: ChatConversationMessage;
}

const ChatConversationMessageRow = memo(function ChatConversationMessageRow({
  msg,
}: ChatConversationMessageRowProps) {
  const user = useMemo(
    () => ({
      name: msg.senderName,
      initials: msg.senderInitials ?? getInitials(msg.senderName, "??"),
    }),
    [msg.senderName, msg.senderInitials]
  );
  const timestamp = useMemo(() => formatChatTimestamp(msg.timestamp), [msg.timestamp]);
  const ChatBubble = msg.isFromCurrentUser ? ChatUserResponse : ChatTeamResponse;
  return (
    <Grid display="flex" flexDirection="column" gap="xs">
      <ChatBubble user={user} message={msg.message} timestamp={timestamp} width="full" />
    </Grid>
  );
});

function ChatConversationPanel({
  messages,
  threadKey,
  emptyStateTitle,
  emptyStateDescription,
  emptyStateIcon,
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder,
  loading = false,
  hasMoreOlderMessages = false,
  loadingOlderMessages = false,
  onLoadMoreOlder,
  showAttachmentIcon = false,
  inputClassName = "chat-conversation-chat-input",
}: ChatConversationPanelProps) {
  const hasMessages = Array.isArray(messages) && messages.length > 0;
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const previousMessageCountRef = useRef(0);
  const shouldStickToBottomRef = useRef(true);
  const lastThreadKeyRef = useRef<string | undefined>(undefined);
  const scrollSignatureRef = useRef("");
  const scrollContainerPrevHeightRef = useRef(0);
  const layoutPrevMetaRef = useRef({ len: 0, lastTail: "", firstHead: "" });

  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      shouldStickToBottomRef.current = distanceFromBottom < 80;
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [threadKey, hasMessages, loading]);

  const lastMessageTailId =
    hasMessages && messages.length > 0 ? String(messages[messages.length - 1]?.id ?? "") : "";
  const firstMessageHeadId =
    hasMessages && messages.length > 0 ? String(messages[0]?.id ?? "") : "";

  useLayoutEffect(() => {
    if (lastThreadKeyRef.current !== threadKey) {
      lastThreadKeyRef.current = threadKey;
      previousMessageCountRef.current = 0;
      shouldStickToBottomRef.current = true;
      scrollSignatureRef.current = "";
      layoutPrevMetaRef.current = { len: 0, lastTail: "", firstHead: "" };
      scrollContainerPrevHeightRef.current = 0;
    }

    if (!hasMessages || loading) {
      if (!hasMessages) scrollSignatureRef.current = "";
      previousMessageCountRef.current = messages.length;
      const elEmpty = messagesScrollRef.current;
      if (elEmpty && hasMessages) scrollContainerPrevHeightRef.current = elEmpty.scrollHeight;
      return undefined;
    }

    const el = messagesScrollRef.current;
    const end = messagesEndRef.current;
    if (!el || !end) return undefined;

    const prevMeta = layoutPrevMetaRef.current;
    const isPrependOlder =
      prevMeta.len > 0 &&
      messages.length > prevMeta.len &&
      lastMessageTailId === prevMeta.lastTail &&
      firstMessageHeadId !== prevMeta.firstHead;

    if (isPrependOlder) {
      const prevScrollHeight = scrollContainerPrevHeightRef.current;
      const newScrollHeight = el.scrollHeight;
      const diff = newScrollHeight - prevScrollHeight;
      if (diff > 0) {
        el.scrollTop += diff;
      }
      shouldStickToBottomRef.current = false;
      scrollSignatureRef.current = `${threadKey ?? ""}|prepend|${messages.length}|${lastMessageTailId}`;
      layoutPrevMetaRef.current = {
        len: messages.length,
        lastTail: lastMessageTailId,
        firstHead: firstMessageHeadId,
      };
      scrollContainerPrevHeightRef.current = newScrollHeight;
      previousMessageCountRef.current = messages.length;
      return undefined;
    }

    const sig = `${threadKey ?? ""}|${messages.length}|${lastMessageTailId}`;
    if (scrollSignatureRef.current === sig) {
      layoutPrevMetaRef.current = {
        len: messages.length,
        lastTail: lastMessageTailId,
        firstHead: firstMessageHeadId,
      };
      scrollContainerPrevHeightRef.current = el.scrollHeight;
      previousMessageCountRef.current = messages.length;
      return undefined;
    }
    scrollSignatureRef.current = sig;

    shouldStickToBottomRef.current = true;
    scrollMessagesToBottom(el, end);
    const clearRetries = scheduleScrollToBottomRetries(el, end);
    layoutPrevMetaRef.current = {
      len: messages.length,
      lastTail: lastMessageTailId,
      firstHead: firstMessageHeadId,
    };
    scrollContainerPrevHeightRef.current = el.scrollHeight;
    previousMessageCountRef.current = messages.length;
    return () => clearRetries();
  }, [threadKey, messages.length, lastMessageTailId, firstMessageHeadId, hasMessages, loading]);

  useEffect(() => {
    if (!hasMessages || loading) return;
    const el = messagesScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (!shouldStickToBottomRef.current) return;
      el.scrollTop = el.scrollHeight;
    });
    const inner = el.firstElementChild;
    if (inner) ro.observe(inner);
    return () => ro.disconnect();
  }, [hasMessages, loading, threadKey, messages.length]);

  return (
    <Grid
      className="chat-conversation-panel sticky-messages-panel"
      display="flex"
      flexDirection="column"
      gap="md"
      flex={1}
      width="100%"
      style={{ minHeight: 0, maxHeight: "100%", height: "100%" }}
      padding="xl"
    >
      <div
        ref={messagesScrollRef}
        key={hasMessages ? (threadKey ?? "default") : "empty"}
        className="chat-conversation-messages-scroll sticky-messages-panel__list"
      >
        {loading ? (
          <div className="chat-conversation-messages-scroll__centered">
            <EmptyState title="Loading messages..." description="Loading messages..." />
          </div>
        ) : !hasMessages ? (
          <div className="chat-conversation-messages-scroll__centered">
            <EmptyState
              icon={emptyStateIcon}
              title={emptyStateTitle}
              description={emptyStateDescription}
            />
          </div>
        ) : (
          <div className="chat-conversation-messages-scroll__fill">
            <Grid display="flex" flexDirection="column" gap="md" width="100%">
              {hasMoreOlderMessages && onLoadMoreOlder ? (
                <Grid display="flex" justifyContent="center" paddingBottom="xs">
                  <Button
                    variant="brand-tertiaryColor"
                    size="sm"
                    disabled={loadingOlderMessages}
                    onClick={() => void onLoadMoreOlder()}
                  >
                    {loadingOlderMessages ? "Loading…" : "Load older messages"}
                  </Button>
                </Grid>
              ) : null}
              {messages.map((msg, index) => (
                <ChatConversationMessageRow key={messageRowKey(msg, index)} msg={msg} />
              ))}
              <div
                ref={messagesEndRef}
                aria-hidden
                className="chat-conversation-messages-end"
                style={{ height: 1, flexShrink: 0 }}
              />
            </Grid>
          </div>
        )}
      </div>

      <div className="chat-conversation-composer sticky-messages-panel__composer">
        <AppChatInput
          className={inputClassName}
          value={value}
          onChange={onChange}
          onSend={onSend}
          disabled={disabled}
          placeholder={placeholder}
          showAttachmentIcon={showAttachmentIcon}
        />
      </div>
    </Grid>
  );
}

const ChatConversationPanelMemo = memo(ChatConversationPanel);
ChatConversationPanelMemo.displayName = "ChatConversationPanel";
export default ChatConversationPanelMemo;
